// libraries
import type { IConfigObj } from '@makemydeal/dr-common-utils';
import type { UrlsConfig } from '@makemydeal/dr-dash-bff-types';
import type {
    InitConfigurationServicesState,
    InitConfigurationState,
    InitConfigurationStaticAssetsState,
    KbbConfig,
    StateTree
} from '@makemydeal/dr-dash-types';
import * as quoteSelectors from './quoteSelectors';

export const getConfig = (state: StateTree): InitConfigurationState => {
    return state.config;
};

export const getConfigStaticImages = (state: StateTree): IConfigObj | undefined => {
    return getConfig(state).staticImages;
};

export const getConfigServices = (state: StateTree): InitConfigurationServicesState => {
    return getConfig(state).services;
};

export const getContractingTimeout = (state: StateTree): number | undefined => {
    return getConfig(state).contracting?.timeout;
};

export const getServicesGateway = (state: StateTree): IConfigObj | undefined => {
    return getConfigServices(state)?.gateway;
};

export const getServicesBff = (state: StateTree): IConfigObj => {
    return getConfigServices(state)?.bff;
};

export const getServicesKbb = (state: StateTree): Partial<KbbConfig> => {
    return getConfigServices(state)?.kbb || ({} as Partial<KbbConfig>);
};

export const getUrls = (state: StateTree): UrlsConfig | undefined => {
    return getConfig(state).urls;
};

export const getStaticAssets = (state: StateTree): InitConfigurationStaticAssetsState | undefined => {
    return getConfig(state).staticAssets;
};

export const getWebsocketUrl = (state: StateTree): string => {
    return getConfigServices(state)?.websockets?.url || '';
};

export const getConnectionFailureLogThreshold = (state: StateTree): number | undefined => {
    const valueInMinutes = getConfigServices(state)?.websockets?.connectionFailureLogThreshold;
    return valueInMinutes ? Number(valueInMinutes) : undefined;
};

export const getHealthCheckInterval = (state: StateTree): number | undefined => {
    const valueInSeconds = getConfigServices(state)?.websockets?.healthCheckInterval;
    return valueInSeconds ? Number(valueInSeconds) : undefined;
};

export const getCopyToDeskingURL = (state: StateTree): string => {
    return getConfig(state).urls?.copyToDesking || '';
};

export const getDealertrackUrl = (state: StateTree): string => {
    return getConfig(state).urls?.dealertrack || '';
};

/**
 * Builds the Dealertrack URL with the provided deal reference ID.
 * @param state
 * @param dealRefId
 * @returns
 */
export const buildDealertrackUrlWithDealRefId = (state: StateTree, dealRefId: string | null): string => {
    const host = getDealertrackUrl(state);
    return `${host}/dealjacket_deal_summary/FD/${dealRefId || ''}/`;
};

export const getVAutoInventoryUrl = (state: StateTree): string => {
    const vAutoUrl = getConfig(state).urls?.vAutoInventory;
    return vAutoUrl || '';
};

export const getVAutoTriforceScriptUrl = (state: StateTree): string => {
    const triforceScriptUrl = getConfig(state).urls?.triforceScriptUrl;
    return triforceScriptUrl || '';
};

export const getVAutoAppraisalUrl = (state: StateTree): string => {
    const appraisalUrl = getConfig(state).urls?.vAutoAppraisal;
    return appraisalUrl || '';
};

export const getTextMessageBaseUrl = (state: StateTree): string => {
    return getConfig(state).urls?.textMessageBaseUrl || '';
};

export const getPartSitesCatalogUrl = (state: StateTree): string => {
    return getConfig(state).urls?.partSites || '';
};

export const getCheckoutAnywhereBaseUrl = (state: StateTree): string => {
    const checkoutAnywhereBaseUrl = getConfig(state).urls?.checkoutAnywhereBaseUrl;
    return checkoutAnywhereBaseUrl || '';
};

export const getDDJSourceUrl = (state: StateTree): string => {
    const ddjSourceUrl = getConfig(state).urls?.ddjSourceUrl;
    return ddjSourceUrl || '';
};

export const getLaunchUrl = (state: StateTree): string => {
    const launchUrl = getConfig(state).urls?.launchUrl;
    return launchUrl || '';
};

export const getPushToDmsLoaderTimeout = (state: StateTree): string => {
    const timeout = getConfig(state).features?.pushToDms?.loaderTimeoutThreshold;
    return timeout || '8';
};

export const getMaxAllowedFutureMonthsInDateField = (state: StateTree): number => {
    const maxAllowedFutureMonthsInDateField = getConfig(state).features?.dealLifeCycle?.maxAllowedFutureMonthsInDateField || '';
    return parseInt(maxAllowedFutureMonthsInDateField) || 3;
};

export const getCDMSPbcUrl = (state: StateTree): string => {
    const cdmsPbcUrl = getConfig(state).features?.pushToDms?.cdmsPbcUrl;
    return cdmsPbcUrl || '';
};

export const getPSErrors = (state: StateTree): { [key: string]: string } => {
    return getConfigServices(state)?.psErrors || {};
};

export const isDealTemplatesViewEnabled = (state: StateTree): boolean => {
    return (getConfig(state)?.additionalProps?.dealTemplatesView || false) && !quoteSelectors.getQuoteMode(state);
};

export const getDealListUrl = (state: StateTree): string => {
    return getConfig(state).urls?.dealListUrl || '';
};

export const getPaymentLambdaUrl = (state: StateTree): string => {
    return getConfigServices(state)?.paymentLambdaUrl || '';
};

export const getCancellationApplicableStates = (state: StateTree): string[] => {
    return getConfig(state).rightToCancel?.applicableStates ?? [];
};
